import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { assignOwner, saveMessage, sourcesKey, type Enveloped, type SourceDetail, type SourcesData } from "./api";

/**
 * Assign an owner (UX F1, action #1): inline on the row, no modal, no page change.
 *
 * Nothing is pre-filled, defaulted or suggested (C1 I-C1-2, BR-010): never the
 * registry's Owner label, the collector or whoever writes the data. The steward
 * types the owner's Ketosource email. (The roster picker is gone with the portal
 * facade, GTASK-172: the roster is portal data and the read seam does not serve
 * it. The service refuses a non-Ketosource address.) The steward names the A7
 * decision record the assignment rests on; Save stays disabled without both.
 *
 * Optimistic (UX §7): on success the row moves under its owner at once, because
 * the registry's BigQuery projection catches up within the hour. On failure the
 * chosen person stays chosen, the picker stays open, and the line says why.
 */
export function AssignControl({ sourceKey, label = "Assign" }: { sourceKey: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState<string>("");
  const [ref, setRef] = useState("");
  const client = useQueryClient();

  const ownerEmail = owner.trim().toLowerCase();
  const ownerValid = /^[^@\s]+@ketosource\.co$/.test(ownerEmail);
  const save = useMutation({
    mutationFn: () => assignOwner(sourceKey, ownerEmail, ref.trim()),
    onSuccess: () => {
      client.setQueryData<Enveloped<SourcesData>>(sourcesKey, (prev) =>
        prev
          ? {
              ...prev,
              data: {
                ...prev.data,
                sources: prev.data.sources.map((s) =>
                  s.source_key === sourceKey ? { ...s, owner_email: ownerEmail, owner_state: "ASSIGNED" } : s,
                ),
              },
            }
          : prev,
      );
      // The detail drill-in reads its own cache entry. Patch it the same way rather than
      // refetch: the BigQuery projection lags C1 by up to an hour, so a refetch would
      // show the source unowned again.
      client.setQueriesData<Enveloped<SourceDetail>>({ queryKey: ["kpi_data_integrity", "source"] }, (prev) =>
        prev && prev.data.source_key === sourceKey
          ? { ...prev, data: { ...prev.data, owner_email: ownerEmail, owner_state: "ASSIGNED" } }
          : prev,
      );
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2" data-assign-panel>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-56"
          type="email"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="Owner, e.g. name@ketosource.co"
          aria-label="Who owns this source"
        />
        <Input
          className="w-56"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="Decision reference, e.g. GTASK-142"
          aria-label="Decision reference"
        />
        <Button size="sm" disabled={!ownerValid || !ref.trim() || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save owner"}
        </Button>
        <Button size="sm" variant="ghost" data-nonmutating onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {save.isError && (
        <Alert>
          <AlertDescription>{saveMessage(save.error)}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
